import { useState, useRef, type ChangeEvent } from "react";
import { DateTime } from "luxon";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import { Icon } from "@/components/ui/Icon";
import {
  useFetchCurrentUserProfileQuery,
  useUpdateUserProfileMutation,
} from "@/api/clubsApi";
import type { UserProfile, UserTopic } from "@/api/clubsApi";
import { useAuth } from "@/components/providers/AuthProvider";
import { cn } from "@/components/ui/cn";
import { uploadImage } from "@/utils/uploadUtils";
import { userDisplayName } from "@tinyclub/shared/utils";
import { EditBasicInfoModal } from "./EditBasicInfoModal";
import { EditIntroModal } from "./EditIntroModal";
import { EditAboutModal } from "./EditAboutModal";
import { EditContactModal } from "./EditContactModal";
import { EditSocialModal } from "./EditSocialModal";
import { EditInterestsModal } from "./EditInterestsModal";

interface EditProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditProfileModal({ open, onOpenChange }: EditProfileModalProps) {
  const { user } = useAuth();
  const { data: profile, isLoading: isLoadingProfile } =
    useFetchCurrentUserProfileQuery(
      { id: user?.id ?? "", includeTopics: true },
      { skip: !user?.id }
    );
  const [updateProfile] = useUpdateUserProfileMutation();

  // Photo upload state
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Nested modal state
  const [editBasicInfoOpen, setEditBasicInfoOpen] = useState(false);
  const [editIntroOpen, setEditIntroOpen] = useState(false);
  const [editAboutOpen, setEditAboutOpen] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [editSocialOpen, setEditSocialOpen] = useState(false);
  const [editInterestsOpen, setEditInterestsOpen] = useState(false);

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    setIsUploadingPhoto(true);
    setPhotoPreview(URL.createObjectURL(file));
    setUploadProgress(0);

    try {
      const result = await uploadImage(file, (progress) => {
        setUploadProgress(progress);
      });

      await updateProfile({
        id: user.id,
        avatar_small: result.imageSmall,
        avatar_medium: result.imageMedium,
        avatar_full: result.imageFull,
        updated_at: DateTime.now().toISO(),
      }).unwrap();

      setPhotoPreview(null);
    } catch (error) {
      console.error("Photo upload failed:", error);
      setPhotoPreview(null);
    } finally {
      setIsUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const displayName = profile ? userDisplayName(profile) : "";
  const avatarUrl = photoPreview || profile?.avatar_full || profile?.avatar_medium;

  // Format joined date
  const joinedDisplay = profile?.created_at
    ? `Joined ${DateTime.fromISO(profile.created_at).toFormat("MMMM yyyy")}`
    : null;

  return (
    <>
      <SidePanel open={open} onOpenChange={onOpenChange}>
        <SidePanelContent>
          <SidePanelHeader title="Edit profile" onClose={() => onOpenChange(false)} />

          {isLoadingProfile ? (
            <div className="flex-1 flex items-center justify-center py-12">
              <Icon name="circle" className="animate-pulse text-text-tertiary" size={24} />
            </div>
          ) : profile ? (
            <SidePanelBody className="space-y-6">
              {/* Profile Photo */}
              <div className="flex justify-center">
                <div className="relative">
                  <button
                    onClick={handlePhotoClick}
                    disabled={isUploadingPhoto}
                    className="relative w-32 h-32 rounded-2xl overflow-hidden border border-border-tertiary focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background"
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={displayName}
                        className={cn(
                          "w-full h-full object-cover",
                          isUploadingPhoto && "opacity-50"
                        )}
                      />
                    ) : (
                      <div className="w-full h-full bg-surface-1 flex items-center justify-center">
                        <Icon name="user" size={48} className="text-text-tertiary" />
                      </div>
                    )}

                    {/* Edit overlay */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white text-body-2-medium">Edit</span>
                    </div>

                    {/* Upload progress indicator */}
                    {isUploadingPhoto && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <div className="text-white text-body-2-medium">
                          {Math.round(uploadProgress * 100)}%
                        </div>
                      </div>
                    )}
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Info Text */}
              <p className="text-body-3 text-text-tertiary text-center">
                TinyClub members will be able to see what you choose to share on your profile.
              </p>

              {/* Basic Info Section */}
              <ProfileSection title="Basic info" onEdit={() => setEditBasicInfoOpen(true)}>
                <div className="bg-surface-1 rounded-2xl">
                  <ProfileRow
                    icon="user"
                    value={formatNameWithPronouns(displayName, profile.pronouns)}
                    placeholder="Add your name"
                    isFirst
                  />
                  <div className="h-px bg-border-subtle mx-4" />
                  <ProfileRow
                    icon="at"
                    value={profile.username ? `@${profile.username}` : null}
                    placeholder="Add username"
                    isLast
                  />
                </div>
              </ProfileSection>

              {/* Intro/Bio Section */}
              <div className="space-y-2">
                <h3 className="text-body-2-medium text-text-primary px-2">Intro</h3>
                <div className="border border-dashed border-border-secondary rounded-2xl p-4 space-y-3">
                  <p
                    className={cn(
                      "text-body-2",
                      profile.bio ? "text-text-secondary" : "text-text-tertiary"
                    )}
                  >
                    {profile.bio || "Share a quick intro about yourself"}
                  </p>
                  <button
                    onClick={() => setEditIntroOpen(true)}
                    className="w-full py-3 bg-surface-2 rounded-2xl text-body-2-medium text-text-primary hover:bg-surface-3 transition-colors"
                  >
                    {profile.bio ? "Edit intro" : "Add intro"}
                  </button>
                </div>
              </div>

              {/* About Section */}
              <ProfileSection title="About" onEdit={() => setEditAboutOpen(true)}>
                <div className="bg-surface-1 rounded-2xl">
                  <ProfileRow
                    icon="house"
                    value={getLocationDisplay(profile)}
                    placeholder="Add location"
                    isFirst
                  />
                  <div className="h-px bg-border-subtle mx-4" />
                  <ProfileRow
                    icon="briefcase"
                    value={profile.job_title}
                    placeholder="Add job title"
                  />
                  <div className="h-px bg-border-subtle mx-4" />
                  <ProfileRow
                    icon="cake"
                    value={formatBirthdayDisplay(profile.birthdate)}
                    placeholder="Add birthday"
                  />
                  <div className="h-px bg-border-subtle mx-4" />
                  <ProfileRow
                    icon="graduation-cap"
                    value={profile.education_name}
                    placeholder="Add education"
                    isLast
                  />
                </div>
              </ProfileSection>

              {/* Contact Section */}
              <ProfileSection title="Contact" onEdit={() => setEditContactOpen(true)}>
                <div className="bg-surface-1 rounded-2xl">
                  <ProfileRow
                    icon="phone"
                    value={formatPhoneNumber(profile.phone_number)}
                    placeholder="Add phone number"
                    isFirst
                  />
                  <div className="h-px bg-border-subtle mx-4" />
                  <ProfileRow
                    icon="envelope"
                    value={profile.email}
                    placeholder="Add email"
                    isLast
                  />
                </div>
              </ProfileSection>

              {/* Social Section */}
              <ProfileSection title="Social" onEdit={() => setEditSocialOpen(true)}>
                <div className="bg-surface-1 rounded-2xl">
                  <ProfileRow
                    icon="instagram"
                    value={profile.instagram}
                    placeholder="Add Instagram"
                    isFirst
                  />
                  <div className="h-px bg-border-subtle mx-4" />
                  <ProfileRow
                    icon="tiktok"
                    value={profile.tiktok}
                    placeholder="Add TikTok"
                  />
                  <div className="h-px bg-border-subtle mx-4" />
                  <ProfileRow
                    icon="linkedin"
                    value={profile.linkedin}
                    placeholder="Add LinkedIn"
                  />
                  <div className="h-px bg-border-subtle mx-4" />
                  <ProfileRow
                    icon="strava"
                    value={profile.strava}
                    placeholder="Add Strava"
                    isLast
                  />
                </div>
              </ProfileSection>

              {/* Interests Section */}
              <ProfileSection title="Interests" onEdit={() => setEditInterestsOpen(true)}>
                <div className="bg-surface-1 rounded-2xl p-4">
                  {profile.user_topics && profile.user_topics.length > 0 ? (
                    <InterestPills userTopics={profile.user_topics} />
                  ) : (
                    <p className="text-body-2 text-text-tertiary">Add your interests</p>
                  )}
                </div>
              </ProfileSection>

              {/* Footer - Joined Date */}
              {joinedDisplay && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <Icon name="circle-check" size={16} className="text-text-tertiary" />
                  <span className="text-body-3 text-text-tertiary">{joinedDisplay}</span>
                </div>
              )}
            </SidePanelBody>
          ) : (
            <div className="flex-1 flex items-center justify-center py-12">
              <p className="text-text-secondary">Profile not found</p>
            </div>
          )}
        </SidePanelContent>
      </SidePanel>

      {/* Nested Edit Modals */}
      {profile && (
        <>
          <EditBasicInfoModal
            open={editBasicInfoOpen}
            onOpenChange={setEditBasicInfoOpen}
            profile={profile}
          />
          <EditIntroModal
            open={editIntroOpen}
            onOpenChange={setEditIntroOpen}
            profile={profile}
          />
          <EditAboutModal
            open={editAboutOpen}
            onOpenChange={setEditAboutOpen}
            profile={profile}
          />
          <EditContactModal
            open={editContactOpen}
            onOpenChange={setEditContactOpen}
            profile={profile}
          />
          <EditSocialModal
            open={editSocialOpen}
            onOpenChange={setEditSocialOpen}
            profile={profile}
          />
          <EditInterestsModal
            open={editInterestsOpen}
            onOpenChange={setEditInterestsOpen}
            profile={profile}
          />
        </>
      )}
    </>
  );
}

// Helper Components

function ProfileSection({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center px-2">
        <h3 className="text-body-2-medium text-text-primary">{title}</h3>
        <button
          onClick={onEdit}
          className="text-body-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          Edit
        </button>
      </div>
      {children}
    </div>
  );
}

function ProfileRow({
  icon,
  value,
  placeholder,
  isFirst,
  isLast,
}: {
  icon: string;
  value: string | null | undefined;
  placeholder: string;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 py-3",
        isFirst && "pt-4",
        isLast && "pb-4"
      )}
    >
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={16} className="text-text-secondary" />
      </div>
      <span
        className={cn("text-body-2", value ? "text-text-primary" : "text-text-tertiary")}
      >
        {value || placeholder}
      </span>
    </div>
  );
}

function InterestPills({ userTopics }: { userTopics: UserTopic[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {userTopics.map((userTopic) => {
        const isSubtopic = !!userTopic.subtopic_id;
        const name = isSubtopic ? userTopic.subtopics?.name : userTopic.topics?.name;
        const emoji = isSubtopic
          ? userTopic.subtopics?.parent_topic?.emoji
          : userTopic.topics?.emoji;

        return (
          <div
            key={userTopic.id}
            className="flex items-center gap-2 px-3 py-2 rounded-full border border-border-tertiary"
          >
            {emoji && <span className="text-base">{emoji}</span>}
            <span className="text-body-2 text-text-primary">{name}</span>
          </div>
        );
      })}
    </div>
  );
}

// Helper Functions

function formatNameWithPronouns(name: string, pronouns: string | null | undefined): string {
  if (!pronouns) return name;
  return `${name} (${pronouns})`;
}

function getLocationDisplay(profile: UserProfile): string | null {
  const locationObject = profile.location_object as {
    properties?: {
      context?: {
        place?: { name?: string };
        region?: { name?: string };
      };
    };
  } | null;

  const locationCity = locationObject?.properties?.context?.place?.name;
  const locationState = locationObject?.properties?.context?.region?.name;

  if (locationCity && locationState) {
    return `${locationCity}, ${locationState}`;
  }

  return profile.location_name || null;
}

function formatBirthdayDisplay(birthdate: string | null | undefined): string | null {
  if (!birthdate) return null;

  try {
    const date = DateTime.fromISO(birthdate);
    if (!date.isValid) return null;
    return date.toFormat("MMMM d");
  } catch {
    return null;
  }
}

function formatPhoneNumber(phone: string | null | undefined): string | null {
  if (!phone) return null;

  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}
